import { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  StateEmailParagraphState,
  useGetEmailTemplateSettings,
  useUpdateEmailTemplateSettings,
  useInvalidateUseGetEmailTemplateSettings,
  type EmailTemplateSettingsResponse,
} from '@urbanx/agx.agencies.client';
import { AgxSpacer, AgxTypography, useToast } from '@urbanx/agx.ui-components2';
import { getEmailTemplateStates } from 'components/settings-section/agencyLicenceStates';
import SettingsPageHeader from 'components/settings-section/SettingsPageHeader';
import { FormPrompt } from 'components/FormPrompt';
import { getErrorMessage } from 'helpers/getErrorMessage';
import { MenuTitles, SettingsMenu } from 'constants/menu';
import { useSelectedAgency } from 'hooks';
import { ContentLayout, PageLayout } from 'layout';
import {
  EmailTemplatesForm,
  emailTypes,
  emailTemplateSettingsSchema,
} from './emailTemplateFormDefinition';
import EmailTemplateSection from './EmailTemplateSection';

export function EmailTemplatesEditor({
  agencyId,
  source,
  states,
  onBack,
}: {
  agencyId: string;
  source: EmailTemplateSettingsResponse;
  states: StateEmailParagraphState[];
  onBack?: () => void;
}) {
  const { pushToast } = useToast();
  const { updateEmailTemplateSettings } = useUpdateEmailTemplateSettings();
  const invalidate = useInvalidateUseGetEmailTemplateSettings();
  const [error, setError] = useState<string>();
  const defaultValues = useMemo(
    () => ({ settings: emailTemplateSettingsSchema.parse(source.settings), eTag: source.eTag }),
    [source]
  );
  const form = EmailTemplatesForm.useForm({
    defaultValues,
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      setError(undefined);
      try {
        const saved = await updateEmailTemplateSettings({
          agencyId,
          body: { configuration: parsedValues.settings, eTag: parsedValues.eTag },
        });
        if (!saved) throw new Error('The server did not return the saved email settings.');
        await invalidate(agencyId);
        pushToast({ type: 'success', message: 'Email templates saved successfully' });
        return {
          status: 'accepted',
          baseline: {
            settings: emailTemplateSettingsSchema.parse(saved.settings),
            eTag: saved.eTag,
          },
        };
      } catch (failure) {
        const message = getErrorMessage(
          failure,
          'Unable to save email templates. Please try again.',
          'Your draft is preserved.'
        );
        setError(message);
        pushToast({ type: 'error', message });
        return { status: 'failed', error: failure };
      }
    },
  });

  return (
    <EmailTemplatesForm.Root form={form}>
      <FormPrompt hasUnsavedChanges={form.isDirty} />
      <AgxSpacer direction="vertical" gap="spacing-2xl" fill>
        <SettingsPageHeader
          id="email-templates"
          title="Email Templates"
          saving={form.phase === 'submitting'}
          disabled={!form.isDirty || !form.canSubmit}
          onSave={() => void form.submit()}
          onBack={onBack}
        />
        {error && <AgxTypography type="form_error">{error}</AgxTypography>}
        {states.length === 0 && (
          <AgxTypography type="body" role="status">
            Email templates are available for agencies with a QLD or NSW licence.
          </AgxTypography>
        )}
        {emailTypes.map(({ key, title, description }) => (
          <EmailTemplatesForm.CustomField key={key} name={`settings.${key}.paragraphs`}>
            {(field) => (
              <EmailTemplateSection
                type={key}
                title={title}
                description={description}
                value={field.value}
                states={states}
                onChange={field.onChange}
                disabled={field.disabled}
                error={field.errorText}
              />
            )}
          </EmailTemplatesForm.CustomField>
        ))}
      </AgxSpacer>
    </EmailTemplatesForm.Root>
  );
}

export default function EmailTemplates() {
  const { userAccount } = useContext(AuthContext);
  const { agencyId } = useParams();
  const { agency } = useSelectedAgency();
  const [activeMenu, setActiveMenu] = useState<MenuTitles | undefined>(MenuTitles.EMAIL_TEMPLATES);
  const { data, isPending, isError } = useGetEmailTemplateSettings(agencyId ?? '', !!agencyId);
  const states = getEmailTemplateStates(agency?.companyDetails?.licences).map(
    (state) => StateEmailParagraphState[state]
  );

  return (
    <PageLayout
      agentName={userAccount?.firstName ?? ''}
      primaryPageTitle={agency?.name ?? ''}
      currentPageTitle={MenuTitles.SETTINGS}
    >
      <ContentLayout
        hasSecondaryMenu
        secondaryActiveMenu={MenuTitles.SETTINGS}
        tertiaryMenu={SettingsMenu}
        tertiaryActiveMenu={activeMenu}
      >
        {isPending && (
          <AgxTypography type="body" role="status">
            Loading email templates…
          </AgxTypography>
        )}
        {isError && (
          <AgxTypography type="form_error" role="alert">
            Unable to load email templates. Reload the page to try again.
          </AgxTypography>
        )}
        {data && agencyId && (
          <EmailTemplatesEditor
            key={agencyId}
            agencyId={agencyId}
            source={data}
            states={states}
            onBack={() => setActiveMenu(undefined)}
          />
        )}
      </ContentLayout>
    </PageLayout>
  );
}
